import React, { useState, useEffect, useMemo, useCallback } from 'react';

interface CheckboxMultiSelectorProps {
  items: Item[];
  selectedValues: string[];
  selectableAll: boolean;
  selectableLabel?: string;
  onChange: (v: string[]) => void;
}

interface Item {
  label: string;
  value: string;
}

const CheckboxMultiSelector: React.FC<CheckboxMultiSelectorProps> = ({
  items,
  selectedValues,
  selectableAll,
  selectableLabel,
  onChange
}) => {
  const [values, setValues] = useState<string[]>(selectedValues);
  const styles = useMemo(() => getStyles(), []);

  const selectedSet = useMemo(() => new Set(values), [values]);

  const isItemChecked = useCallback(
    (itemValue: string): boolean => {
      return selectedSet.has(itemValue);
    },
    [selectedSet]
  );

  useEffect(() => {
    setValues(selectedValues);
  }, [selectedValues]);

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;

      setValues(prev => {
        const newSelectedValues = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
        onChange(newSelectedValues);
        return newSelectedValues;
      });
    },
    [onChange]
  );

  const handleCheckAll = useCallback(() => {
    setValues(() => {
      const newSelectedValues = items.map(item => item.value);
      onChange(newSelectedValues);
      return newSelectedValues;
    });
  }, [items, onChange]);

  const handleClearAll = useCallback(() => {
    setValues(() => {
      const newSelectedValues: string[] = [];
      onChange(newSelectedValues);
      return newSelectedValues;
    });
  }, [onChange]);

  return (
    <div>
      {selectableAll && (
        <div>
          <button type="button" onClick={handleCheckAll} style={styles.linkButton}>
            {selectableLabel ? `全ての${selectableLabel}` : '全てを選択'}
          </button>{' '}
          |{' '}
          <button type="button" onClick={handleClearAll} style={styles.linkButton}>
            全てクリア
          </button>
        </div>
      )}
      <ul>
        {items.map(item => (
          <li key={item.value}>
            <label htmlFor={`checkbox-${item.value}`}>
              <input
                id={`checkbox-${item.value}`}
                type="checkbox"
                value={item.value}
                onChange={handleChange}
                checked={isItemChecked(item.value)}
              />
              {item.label}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CheckboxMultiSelector;

function getStyles() {
  return {
    linkButton: {
      fontSize: '12px',
      color: '#a7a7a7',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      textDecoration: 'underline'
    } as React.CSSProperties
  } as const;
}
